


import React, { useState, useEffect } from 'react';
import { Download, ChevronLeft, ChevronRight, ChevronDown, Calendar, Target, FileText, Share2, X, MessageCircle, Eye, Utensils } from 'lucide-react';
import { DatePicker } from 'antd';
import { CalendarOutlined, CaretDownOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { resolvePdfUrl } from '../../api/Services/resolvePdfUrl';

const SMART_DIET_ROWS_PER_PAGE = 10;
const SMART_DIET_FETCH_SIZE = 500;

const WhatsAppShareModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [error, setError] = useState("");

  const handleShare = () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!mobileNumber.trim()) {
      setError("Please enter a mobile number");
      return;
    }
    if (!phoneRegex.test(mobileNumber.trim())) {
      setError("Please enter a valid 10-digit mobile number starting with 6-9");
      return;
    }
    setError("");
    onConfirm(mobileNumber.trim());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
  <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl">
    
    {/* Header */}
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-xl font-bold text-cyan-600 flex items-center gap-2">
        <MessageCircle className="h-6 w-6 text-green-600" />
        Share via WhatsApp
      </h3>
      <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
        <X className="h-6 w-6" />
      </button>
    </div>

    {/* Description */}
    <p className="text-sm text-gray-500 mb-4">
      Enter the mobile number where you want to send the diet plan PDF.
    </p>

    {/* Input */}
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-600 mb-2">
        Mobile Number (10 digits)
      </label>
      <input
        type="tel"
        value={mobileNumber}
        onChange={(e) => {
          setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10));
          setError("");
        }}
        placeholder="Enter 10-digit number"
        maxLength="10"
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
      />
      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
    </div>

    {/* Buttons */}
    <div className="flex gap-3">
      <button
        onClick={onClose}
        disabled={loading}
        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-100 disabled:opacity-50"
      >
        Cancel
      </button>

      <button
        onClick={handleShare}
        disabled={loading}
        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Sending...
          </>
        ) : (
          <>
            <MessageCircle className="h-4 w-4" />
            Send
          </>
        )}
      </button>
    </div>

  </div>
</div>
  );
};

const renderCureBayHighlight = (text) => {
  if (!text || typeof text !== "string") return text;
  const parts = text.split("CureBay");
  if (parts.length === 1) return text;
  const nodes = [];
  for (let i = 0; i < parts.length; i++) {
    nodes.push(parts[i]);
    if (i < parts.length - 1) {
      nodes.push(
  <a
    key={`cb-${i}`}
    href="https://curebay.com/services/book-an-appointment"
    target="_blank"
    rel="noopener noreferrer"
    className="font-bold text-[#01bd7f] hover:text-[#00a86b] underline cursor-pointer transition-colors"
    style={{ fontSize: '14px' }}
  > 
    CureBay
  </a>
);
    }
  }
  return nodes;
};

const SwapMealModal = ({
  isOpen, onClose, day, mealType, primaryMeal, alternativeMeal, originalMeal, onConfirm
}) => {
  const [selectedMeal, setSelectedMeal] = useState(null);

  if (!isOpen) return null;

  const options = [
    { id: 0, meal: alternativeMeal, label: "Alternative" },
    { id: 1, meal: originalMeal || primaryMeal, label: "Original" }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-[#ffffff] rounded-lg p-4 max-w-sm w-full shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-[#00CFA5]">Swap Meal</h3>
          <button onClick={onClose} className="text-[#6b7280] hover:text-[#00CFA5] transition">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mb-4">
          <p className="text-xs text-[#6b7280] mb-1">
            You're swapping: <strong>{day} - {mealType}</strong>
          </p>
          <div className="bg-[#f9fafb] p-2 rounded">
            <p className="text-[11px] text-[#6b7280]">Current meal</p>
            <p className="text-xs font-medium text-[#00CFA5] line-clamp-2">{primaryMeal}</p>
          </div>
        </div>
        <div className="mb-4">
          <p className="text-xs font-semibold text-[#00CFA5] mb-2">Choose another option:</p>
          <div className="space-y-2 max-h-36 overflow-y-auto">
            {options.map((option) => (
              <label
                key={option.id}
                className="flex gap-2 p-2 border border-[#e5e7eb] rounded-md hover:bg-[#f0fdfb] cursor-pointer"
              >
                <input
                  type="radio"
                  name="meal-swap"
                  checked={selectedMeal === option.id}
                  onChange={() => setSelectedMeal(option.id)}
                  className="mt-1 accent-[#00CFA5]"
                />
                <div>
                  <p className="text-[11px] text-[#6b7280] font-medium">{option.label}</p>
                  <p className="text-xs text-[#374151] line-clamp-2">{option.meal}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-1.5 border border-[#e5e7eb] rounded-md text-xs text-[#6b7280] hover:bg-[#f3f4f6]"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedMeal !== null) {
                onConfirm(options[selectedMeal].meal);
                setSelectedMeal(null);
              }
            }}
            disabled={selectedMeal === null}
            className="flex-1 px-3 py-1.5 bg-[#00CFA5] text-white text-xs rounded-md hover:bg-[#00B594] disabled:opacity-50"
          >
            Confirm Swap
          </button>
        </div>
      </div>
    </div>
  );
};

const Collapsible = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between rounded-lg border p-3 hover:bg-gray-50 transition"
        style={{ borderColor: '#00CFA5', backgroundColor: '#FFFFFF' }}
      >
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <h3 className="text-sm font-bold" style={{ color: '#00CFA5' }}>{title}</h3>
        </div>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          style={{ color: '#00CFA5' }}
        />
      </button>
      {isOpen && <div className="rounded-lg bg-[#ffffff] p-3 space-y-3 border border-gray-200">{children}</div>}
    </div>
  );
};

const DietDetailsModal = ({ isOpen, onClose, diet, swapModal, setSwapModal, onSwapConfirm }) => {
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [selectedMeals, setSelectedMeals] = useState({});

  const reports = diet?.reports || {};
  const weeklyPlan = reports.weekly_plan || {};
  const macros = reports.macros || {};
  const goalInsights = reports.goal_insight || {};
  const recommendations = reports.recommndation || [];
  const pdfReports = reports.pdf_report || [];
  const mentalHealthSupport = reports.mental_health_support || [];
  const smartSwaps = reports.smart_swap || goalInsights.smart_swaps || [];
  const recipe = reports.recipe || goalInsights.recipe || {};
  const days = Object.keys(weeklyPlan);

  const openSwapModal = (day, mealType, currentMeal, alternative, original) => {
    setSwapModal({ isOpen: true, day, mealType, currentMeal, alternative, original });
  };

  const handleLocalSwapConfirm = (newMeal) => {
    const { day, mealType } = swapModal;
    setSelectedMeals(prev => ({
      ...prev,
      [day]: { ...prev[day], [mealType]: newMeal }
    }));
    onSwapConfirm(newMeal);
  };

  useEffect(() => {
    if (days.length > 0) setSelectedDay(days[0]);
  }, [diet]);

  if (!isOpen || !diet) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-3 sm:p-4 pt-20 sm:pt-24">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl">

        {/* Header */}
       <div className="bg-gradient-to-r from-[#00CFA5] to-[#00B5E2] p-3 sm:p-4 flex justify-between items-center rounded-t-lg flex-shrink-0">
  <div className="flex-1 min-w-0">
    <h2 className="text-base sm:text-lg font-bold truncate text-[#004276]">
      Diet Plan Details
    </h2>
    <p className="text-xs text-white opacity-90 mt-0.5">
      Order: <span className="font-mono text-sm font-semibold">{diet.order_id}</span>
    </p>
  </div>
  <button onClick={onClose} className="ml-2 p-1.5 text-white hover:bg-white/20 rounded-full transition flex-shrink-0">
    <X className="h-4 w-4 sm:h-5 sm:w-5" />
  </button>
</div>

        <div className="overflow-y-auto flex-1 p-3 sm:p-4 space-y-3 bg-[#ffffff]">

          {/* Patient Info */}
          <div className="bg-gradient-to-r from-[#E3F2FD] to-[#E0F2F1] p-3 rounded-lg border-l-4 border-[#00CFA5]">
            <h3 className="font-semibold text-[#1f2937] mb-2 text-xs flex items-center gap-2">
              <span>👤</span> Patient Information
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <span className="text-[#6b7280] block">Name</span>
                <p className="font-medium text-[#1f2937] truncate">{diet.patient_name}</p>
              </div>
              <div>
                <span className="text-[#6b7280] block">Goal</span>
                <p className="font-medium text-[#1f2937] break-words capitalize">{diet.goal}</p>
              </div>
              <div>
                <span className="text-[#6b7280] block">Mobile</span>
                <p className="font-medium text-[#1f2937]">{diet.mobile}</p>
              </div>
              <div>
                <span className="text-[#6b7280] block">Patient ID</span>
                <p className="font-medium text-[#1f2937] truncate">{diet.patient_id}</p>
              </div>
            </div>
          </div>

          {/* Overview */}
          <Collapsible title="Overview" icon="📊" defaultOpen>
            <div className="space-y-3">
              {recommendations.length > 0 && (
                  <div className="p-2.5 rounded-lg border-l-4 border-[#42BA85] bg-[#F0FDF4]">
                    <h4 className="text-xs font-semibold text-[#166534] mb-2 flex items-center gap-1">
                      <span>✅</span> Recommendations
                    </h4>
                    <ul className="flex flex-col gap-1.5">
                  {recommendations
                    .filter(rec => typeof rec === 'string' ? rec.trim() : true)
                    .filter(rec => String(rec).replace(/\s/g, '').length > 0)
                    .map((rec, idx) => (
                      <li key={idx} className="text-xs text-[#374151] flex gap-2" style={{ alignItems: 'baseline' }}>
                        <span className="text-[#42BA85] flex-shrink-0">•</span>
                        <span className="flex-1">{renderCureBayHighlight(rec)}</span>
                      </li>
                    ))}
                </ul>
                  </div>
              )}
              {macros && Object.keys(macros).length > 0 && (
                <div className="p-2.5 rounded-lg border-l-4 border-[#00CFA5] bg-[#F0F9FF]">
                  <h4 className="text-xs font-semibold text-[#0369a1] mb-2 flex items-center gap-1">
                    <span>⚡</span> Daily Nutrition
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {macros.protein_g && (
                      <div className="bg-white p-2 rounded border border-[#86efac] text-center">
                        <p className="text-[10px] text-[#6b7280]">Protein</p>
                        <p className="text-sm font-bold text-[#16a34a]">{macros.protein_g}g</p>
                      </div>
                    )}
                    {macros.carbs_g && (
                      <div className="bg-white p-2 rounded border border-[#fdba74] text-center">
                        <p className="text-[10px] text-[#6b7280]">Carbs</p>
                        <p className="text-sm font-bold text-[#ea580c]">{macros.carbs_g}g</p>
                      </div>
                    )}
                    {macros.calories && (
                      <div className="bg-white p-2 rounded border border-[#93c5fd] text-center">
                        <p className="text-[10px] text-[#6b7280]">Calories</p>
                        <p className="text-sm font-bold text-[#2563eb]">{macros.calories} kcal</p>
                      </div>
                    )}
                    {macros.fat_g && (
                      <div className="bg-white p-2 rounded border border-[#c4b5fd] text-center">
                        <p className="text-[10px] text-[#6b7280]">Fats</p>
                        <p className="text-sm font-bold text-[#9333ea]">{macros.fat_g}g</p>
                      </div>
                    )}
                    {macros.fiber_g && (
                      <div className="bg-white p-2 rounded border border-[#a3e635] text-center">
                        <p className="text-[10px] text-[#6b7280]">Fiber</p>
                        <p className="text-sm font-bold text-[#65a30d]">{macros.fiber_g}g</p>
                      </div>
                    )}
                    {macros.water_ml && (
                      <div className="bg-white p-2 rounded border border-[#67e8f9] text-center">
                        <p className="text-[10px] text-[#6b7280]">Water</p>
                        <p className="text-sm font-bold text-[#0891b2]">{macros.water_ml} ml</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {goalInsights.nutrient_status && Object.keys(goalInsights.nutrient_status).length > 0 && (
                <div className="p-2.5 rounded-lg border-l-4 border-[#004276] bg-[#F1F8FD]">
                  <h4 className="text-xs font-semibold text-[#004276] mb-2 flex items-center gap-1">
                    <span>💊</span> Sources of Nutrients
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {Object.entries(goalInsights.nutrient_status).map(([nutrient, info]) => (
                      <div key={nutrient} className="bg-white p-2 rounded border border-[#C9EEF8]">
                        <p className="text-xs font-semibold text-[#374151] mb-1.5">{nutrient}</p>
                        <div className="text-[10px]">
                          <p className="font-semibold text-[#6b7280] mb-1">Sources:</p>
                          <ul className="text-[#6b7280] space-y-0.5">
                            {info.food_sources && Array.isArray(info.food_sources) ? (
                              info.food_sources.slice(0, 3).map((source, idx) => (
                                <li key={idx} className="flex gap-1">
                                  <span>•</span>
                                  <span>{source}</span>
                                </li>
                              ))
                            ) : (
                              <li>No sources available</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {goalInsights.goal_summary && (
                <div className="p-2.5 rounded-lg bg-[#dbeafe] border border-[#93c5fd]">
                  <h4 className="text-xs font-semibold text-[#1e40af] mb-1 flex items-center gap-1">
                    <span>🎯</span> Goal Summary
                  </h4>
                  <p className="text-xs text-[#374151] leading-relaxed">{goalInsights.goal_summary}</p>
                </div>
              )}
            </div>
          </Collapsible>

          {/* Weekly Plan */}
          {days.length > 0 && (
            <Collapsible title="Weekly Plan" icon="📅" defaultOpen>
              <div className="space-y-2">
                <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-thin">
                  {days.map((day) => (
                    <button
                      key={day}
                      onClick={() => setSelectedDay(day)}
                      className={`px-2.5 py-1.5 rounded-full text-[10px] font-medium whitespace-nowrap transition flex-shrink-0 ${
                        selectedDay === day
                          ? "bg-[#00796B] text-white"
                          : "bg-white text-[#374151] border border-[#d1d5db] hover:bg-[#f3f4f6]"
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  {weeklyPlan[selectedDay] &&
                    Object.entries(weeklyPlan[selectedDay]).map(([mealType, mealData]) => {
                      if (mealType === "Daily Nutrient Summary") {
                        return (
                          <div key={mealType} className="bg-[#f0fdf4] p-2 rounded-lg border-l-4 border-[#22c55e]">
                            <h5 className="font-semibold text-[#166534] text-[10px] mb-1">📊 Daily Summary</h5>
                            <p className="text-xs text-[#374151]">
                              {typeof mealData === "string" ? mealData : mealData.summary || mealData}
                            </p>
                          </div>
                        );
                      }

                      const primaryMeal = mealData.primary || mealData;
                      const alternativeMeal = mealData.alternative || "No alternative available";

                      return (
                        <div key={mealType} className="bg-white border border-[#e5e7eb] rounded-lg overflow-hidden">
                          <div className="bg-[#eff6ff] p-2 flex items-center justify-between border-b border-[#dbeafe]">
                            <div className="flex items-center gap-2 flex-1">
                              <Utensils className="h-3 w-3 text-[#2563eb] flex-shrink-0" />
                              <h5 className="font-semibold text-[#1e40af] text-xs truncate">{mealType}</h5>
                            </div>
                            {alternativeMeal !== "No alternative available" && (
                              <button
                                onClick={() => {
                                  const currentMeal = selectedMeals?.[selectedDay]?.[mealType] || primaryMeal;
                                  openSwapModal(selectedDay, mealType, currentMeal, alternativeMeal, primaryMeal);
                                }}
                                className="text-[#2563eb] hover:text-[#1e40af] font-medium text-[10px] border border-[#2563eb] px-2 py-1 rounded hover:bg-[#dbeafe] whitespace-nowrap ml-2"
                              >
                                ⟲ Swap
                              </button>
                            )}
                          </div>
                          <div className="p-2 bg-[#fefce8] border-l-4 border-[#eab308]">
                            <p className="text-xs text-[#374151] leading-relaxed">
                              {selectedMeals?.[selectedDay]?.[mealType] || primaryMeal}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              <SwapMealModal
                isOpen={swapModal?.isOpen || false}
                onClose={() => setSwapModal({ isOpen: false, day: "", mealType: "", currentMeal: "", alternative: "", original: "" })}
                day={swapModal?.day || ""}
                mealType={swapModal?.mealType || ""}
                primaryMeal={swapModal?.currentMeal || ""}
                alternativeMeal={swapModal?.alternative || ""}
                originalMeal={swapModal?.original || ""}
                onConfirm={handleLocalSwapConfirm}
              />
            </Collapsible>
          )}

          {/* Holistic Insights */}
          {(mentalHealthSupport.length > 0 || smartSwaps.length > 0 || recipe.title) && (
            <Collapsible title="Holistic Insights" icon="🧘">
              <div className="space-y-2.5">
                {mentalHealthSupport.length > 0 && (
                  <div className="border-l-4 border-[#004276] bg-[#F1F8FD] p-2.5 rounded-lg">
                    <h4 className="text-xs font-semibold text-[#004276] mb-2">🧠 Mental Wellness</h4>
                    <ul className="space-y-1">
                      {mentalHealthSupport.map((tip, idx) => (
                        <li key={idx} className="text-xs text-[#374151] flex gap-2">
                          <span className="text-[#004276] flex-shrink-0">✓</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {smartSwaps.length > 0 && (
                  <div className="border-l-4 border-[#22c55e] bg-[#f0fdf4] p-2.5 rounded-lg">
                    <h4 className="text-xs font-semibold text-[#166534] mb-2">💡 Smart Swaps</h4>
                    <div className="space-y-1.5">
                      {smartSwaps.map((swap, idx) => (
                        <div key={idx} className="text-xs bg-white p-2 rounded border border-[#86efac] flex items-start gap-2">
                          <span className="text-[#22c55e] flex-shrink-0 mt-0.5">→</span>
                          <span className="text-[#374151]">{swap}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {recipe.title && (
                  <div className="border-l-4 border-[#f97316] bg-[#fff7ed] p-2.5 rounded-lg">
                    <h4 className="text-xs font-semibold text-[#c2410c] mb-2">🍳 {recipe.title}</h4>
                    <div className="space-y-2">
                      {recipe.ingredients && recipe.ingredients.length > 0 && (
                        <div className="bg-white p-2 rounded border border-[#fed7aa]">
                          <h5 className="text-[10px] font-semibold mb-1 text-[#9a3412]">📦 Ingredients</h5>
                          <ul className="text-xs space-y-0.5 text-[#374151]">
                            {recipe.ingredients.map((ing, idx) => (
                              <li key={idx} className="flex gap-1.5">
                                <span className="text-[#f97316] flex-shrink-0">•</span>
                                <span>{ing}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {recipe.instructions && recipe.instructions.length > 0 && (
                        <div className="bg-white p-2 rounded border border-[#fed7aa]">
                          <h5 className="text-[10px] font-semibold mb-1 text-[#9a3412]">👨‍🍳 Instructions</h5>
                          <ol className="text-xs space-y-1 text-[#374151]">
                            {recipe.instructions.map((instruction, idx) => (
                              <li key={idx} className="flex gap-2">
                                <span className="text-[#f97316] font-semibold flex-shrink-0">{idx + 1}.</span>
                                <span>{instruction}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {recipe.nutrition_per_serving && (
                        <div className="bg-white p-2 rounded border border-[#fed7aa]">
                          <h5 className="text-[10px] font-semibold mb-1.5 text-[#9a3412]">📊 Nutrition Per Serving</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {recipe.nutrition_per_serving.calories && (
                              <div className="text-xs">
                                <span className="text-[#6b7280]">Calories:</span>
                                <span className="font-bold text-[#374151] ml-1">{recipe.nutrition_per_serving.calories}</span>
                              </div>
                            )}
                            {recipe.nutrition_per_serving.protein && (
                              <div className="text-xs">
                                <span className="text-[#6b7280]">Protein:</span>
                                <span className="font-bold text-[#374151] ml-1">{recipe.nutrition_per_serving.protein}</span>
                              </div>
                            )}
                            {recipe.nutrition_per_serving.carbs && (
                              <div className="text-xs">
                                <span className="text-[#6b7280]">Carbs:</span>
                                <span className="font-bold text-[#374151] ml-1">{recipe.nutrition_per_serving.carbs}</span>
                              </div>
                            )}
                            {recipe.nutrition_per_serving.fat && (
                              <div className="text-xs">
                                <span className="text-[#6b7280]">Fat:</span>
                                <span className="font-bold text-[#374151] ml-1">{recipe.nutrition_per_serving.fat}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {(recipe.cooking_time || recipe.servings) && (
                        <div className="flex gap-3 text-xs text-[#6b7280] bg-white p-2 rounded border border-[#fed7aa]">
                          {recipe.cooking_time && (
                            <div className="flex items-center gap-1">
                              <span>⏱️</span>
                              <span>{recipe.cooking_time}</span>
                            </div>
                          )}
                          {recipe.servings && (
                            <div className="flex items-center gap-1">
                              <span>🍽️</span>
                              <span>{recipe.servings} servings</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Collapsible>
          )}

          {/* Medical Reports */}
          {pdfReports.length > 0 && (
            <Collapsible title="Medical Highlights" icon="📋">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {pdfReports.map((report, idx) => {
                  let bgColor = "#f0fdf4";
                  let borderColor = "#22c55e";
                  if (report.status === "High") { bgColor = "#fef3c7"; borderColor = "#eab308"; }
                  else if (report.status === "Low") { bgColor = "#fee2e2"; borderColor = "#ef4444"; }
                  return (
                    <div key={idx} className="p-2 rounded-lg border-l-4 text-xs" style={{ backgroundColor: bgColor, borderColor }}>
                      <div className="flex justify-between items-start mb-1">
                        <h5 className="font-semibold text-[#1f2937] text-[10px] flex-1 truncate pr-1">{report.parameter}</h5>
                        <span className="px-1.5 py-0.5 bg-white rounded text-[10px] font-medium flex-shrink-0">{report.status}</span>
                      </div>
                      <p className="text-sm font-bold text-[#111827]">{report.value}</p>
                      <p className="text-[10px] text-[#6b7280] mt-0.5">Ref: {report.reference_range}</p>
                    </div>
                  );
                })}
              </div>
            </Collapsible>
          )}
        </div>

        {/* Footer */}
        <div className="bg-[#f9fafb] border-t border-[#e5e7eb] p-3 flex justify-end rounded-b-lg flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#e5e7eb] text-[#374151] rounded-lg hover:bg-[#d1d5db] transition font-medium text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default function MySmartDiets() {
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [whatsappModal, setWhatsappModal] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [selectedDiet, setSelectedDiet] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedMeals, setSelectedMeals] = useState({});
  const [swapModal, setSwapModal] = useState({
    isOpen: false, day: "", mealType: "", currentMeal: "", alternative: "", original: ""
  });

  // ✅ Read from localStorage — same pattern as SmartVitalsChecker
  const storedData = JSON.parse(localStorage.getItem("loginObj"));
  const [patientId] = useState(storedData?.code || sessionStorage.getItem("patientCode") || '');

  const [dateRange, setDateRange] = useState([dayjs().subtract(1, "year"), dayjs()]);
  const [currentPage, setCurrentPage] = useState(1);

  const handleDateRangeChange = (vals) => {
    setDateRange(vals || []);
    setCurrentPage(1);
  };

  const handleSwapConfirm = (newMeal) => {
    const { day, mealType } = swapModal;
    setSelectedMeals(prev => ({
      ...prev,
      [day]: { ...prev[day], [mealType]: newMeal }
    }));
    setSwapModal({ isOpen: false, day: "", mealType: "", currentMeal: "", alternative: "", original: "" });
  };

  // ✅ Show error immediately if no patientId found
  useEffect(() => {
    if (!patientId) {
      setError('Patient ID not found. Please log in again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (patientId) fetchOldDiets();
  }, [patientId, dateRange]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchOldDiets = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        patientid: patientId,
        page_size: SMART_DIET_FETCH_SIZE.toString(),
        page_number: "1"
      });
      if (Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }
      const url = `${process.env.REACT_APP_SMARTDIET_BASEURL}get_smart_diet_list?${params.toString()}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.data && Array.isArray(data.data)) {
        setDiets(data.data);
        setCurrentPage(1);
        setError(null);
      } else {
        setError('No diet plans found');
        setDiets([]);
      }
    } catch (err) {
      setError(`Error loading diet history: ${err.message}`);
      setDiets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (pdf, orderid) => {
    if (!pdf) { setToast({ message: "PDF not available", type: "error" }); return; }
    try {
      const resolvedUrl = await resolvePdfUrl(pdf);
      if (!resolvedUrl) { setToast({ message: "PDF not available", type: "error" }); return; }
      const link = document.createElement('a');
      link.href = resolvedUrl;
      link.download = `Diet_Plan_${orderid}.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setToast({ message: "Error downloading PDF", type: "error" });
    }
  };

  const handleShareClick = (diet) => { setSelectedDiet(diet); setWhatsappModal(true); };
  const handleViewClick = (diet) => { setSelectedDiet(diet); setViewModal(true); };

  const handleWhatsAppShare = async (mobileNumber) => {
    if (!selectedDiet) { setToast({ message: "No diet selected", type: "error" }); return; }
    setWhatsappLoading(true);
    try {
      const resolvedPdfUrl = await resolvePdfUrl(selectedDiet.diet_pdf);
      if (!resolvedPdfUrl) { setToast({ message: "PDF not available", type: "error" }); setWhatsappLoading(false); return; }
      const params = new URLSearchParams({
        patientid: patientId,
        orderid: selectedDiet.order_id,
        pdf_url: resolvedPdfUrl,
        mobile: mobileNumber,
      });
      const response = await fetch(
        `${process.env.REACT_APP_SMARTDIET_BASEURL}get_smart_diet_whatsapp?${params.toString()}`,
        { method: "GET" }
      );
      const responseData = await response.json();
      if (response.ok && responseData.status === "success") {
        setToast({ message: "PDF successfully sent to WhatsApp!", type: "success" });
        setWhatsappModal(false);
        setSelectedDiet(null);
      } else {
        setToast({ message: responseData.message || "Failed to send PDF", type: "error" });
      }
    } catch (err) {
      setToast({ message: "Error sending PDF. Please try again.", type: "error" });
    } finally {
      setWhatsappLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateString; }
  };

  const totalPages = Math.ceil(diets.length / SMART_DIET_ROWS_PER_PAGE);
  const pagedDiets = diets.slice(
    (currentPage - 1) * SMART_DIET_ROWS_PER_PAGE,
    currentPage * SMART_DIET_ROWS_PER_PAGE
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...'); pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1); pages.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1); pages.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push('...'); pages.push(totalPages);
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00ACC1] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your diet history...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-lightBlue to-White p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-[#00ACC1]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#00ACC1]" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-[#004276]">
  My Diet Plans
</h1>
                <p className="text-gray-600">View and download your previous diet recommendations</p>
                {patientId && <p className="text-xs text-gray-500 mt-1">Patient ID: {patientId}</p>}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {!loading && patientId && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-White rounded-lg shadow-sm p-4 mb-4">
              <div style={{ position: "relative", display: "inline-block" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: 220, height: 40, padding: "0 14px", background: "#fff", border: "1px solid #d9d9d9", borderRadius: "50px", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CalendarOutlined style={{ color: "var(--color-text-subheading)", fontSize: "14px" }} />
                    <span style={{ color: dateRange?.length ? "#000" : "#bfbfbf", fontSize: "13px" }}>
                      {dateRange?.length === 2
                        ? `${dateRange[0].format("DD MMM 'YY")} - ${dateRange[1].format("DD MMM 'YY")}`
                        : "Date"}
                    </span>
                  </div>
                  {dateRange?.length === 2 ? (
                    <CloseCircleOutlined style={{ color: "var(--color-text-subheading)", fontSize: "13px", zIndex: 10, position: "relative" }} onClick={(e) => { e.stopPropagation(); handleDateRangeChange([]); }} />
                  ) : (
                    <CaretDownOutlined style={{ color: "var(--color-text-subheading)", fontSize: "12px" }} />
                  )}
                </div>
                <DatePicker.RangePicker
                  onChange={handleDateRangeChange}
                  disabledDate={(current) => current && current > dayjs().endOf("day")}
                  value={dateRange?.length === 2 ? dateRange : null}
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", zIndex: 1 }}
                />
              </div>
            </div>
          )}

          {!loading && diets.length === 0 && !error && (
            <div className="bg-White rounded-lg shadow-sm p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No diet plans yet</h3>
              <p className="text-gray-600">
                {dateRange?.length === 2 ? "No diet plans found for the selected date range" : "Your diet plans will appear here once created"}
              </p>
            </div>
          )}

          {!loading && diets.length > 0 && (
            <div className="space-y-4">
              <div className="bg-White rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Order ID</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Health Goals</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 min-w-[220px] whitespace-nowrap">Created Date</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {pagedDiets.map((diet, idx) => (
                        <tr key={diet.order_id || idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-800">{diet.order_id}</code>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-start gap-2">
                              <Target className="w-4 h-4 text-[#00ACC1] mt-1 flex-shrink-0" />
                              <p className="text-sm text-gray-800 font-medium capitalize">{diet?.goal || "Improve Health"}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 min-w-[220px] whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              {formatDate(diet.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleViewClick(diet)}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-[#00acc1] text-white rounded-lg hover:bg-[#0097a7] transition-colors text-sm font-medium"
                              >
                                <Eye className="w-4 h-4 text-white" />
                                View
                              </button>
                              {diet.diet_pdf && diet.diet_pdf.trim() !== '' ? (
                                <>
                                  <button
                                    onClick={() => handleDownload(diet.diet_pdf, diet.order_id)}
                                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#00acc1] text-White rounded-lg hover:bg-[#0097a7] transition-colors text-sm font-medium"
                                  >
                                    <Download className="w-4 h-4 text-White" />
                                    Download
                                  </button>
                                  <button
                                    onClick={() => handleShareClick(diet)}
                                    className="inline-flex items-center gap-2 px-3 py-2 bg-secondaryColor text-White rounded-lg hover:bg-[#379f71] transition-colors text-sm font-medium"
                                  >
                                    <Share2 className="w-4 h-4 text-White" />
                                    Share
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="bg-White rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    {getPageNumbers().map((page, idx) => (
                      <button
                        key={idx}
                        onClick={() => typeof page === 'number' && handlePageChange(page)}
                        disabled={page === '...'}
                        className={`min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          page === currentPage
                            ? 'bg-[#00ACC1] text-White'
                            : page === '...'
                            ? 'cursor-default text-gray-400'
                            : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  <div className="text-center mt-3 text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DietDetailsModal
        isOpen={viewModal}
        onClose={() => { setViewModal(false); setSelectedDiet(null); }}
        diet={selectedDiet}
        swapModal={swapModal}
        setSwapModal={setSwapModal}
        onSwapConfirm={handleSwapConfirm}
      />

      <WhatsAppShareModal
        isOpen={whatsappModal}
        onClose={() => { setWhatsappModal(false); setSelectedDiet(null); }}
        onConfirm={handleWhatsAppShare}
        loading={whatsappLoading}
      />

      {toast && (
        <div className="fixed top-20 right-4 z-50 animate-in fade-in slide-in-from-right-4 duration-300">
          <div className={`${
            toast.type === 'success'
              ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-200 text-green-800'
              : 'bg-gradient-to-r from-red-100 to-rose-100 border-red-200 text-red-800'
          } border-l-4 rounded-lg shadow-lg p-4 max-w-sm flex items-center gap-3`}>
            <span className="text-lg">{toast.type === 'success' ? '✓' : '⚠'}</span>
            <p className="text-sm font-medium flex-1">{toast.message}</p>
            <button onClick={() => setToast(null)} className="ml-2 opacity-70 hover:opacity-100">✕</button>
          </div>
        </div>
      )}
    </>
  );
}